import type { Identified } from "@iyk/prelude"
import * as UI from "@iyk/ui"
import type { Show } from "@iyk/vf-core"
import { Analytics, useAnalytics } from "@iyk/vf-observability/react"
import { Res } from "@iyk/vf-server"
import { PageContainer } from "@iyk/vf-ui"
import { Data, Effect, Option } from "effect"
import * as React from "react"
import { useRevalidator } from "react-router"
import { appConfig } from "../app-config.ts"
import { GlobalRoutes } from "../react-router/routes.ts"
import { Handler } from "../server/handler.ts"
import { HeaderLogo } from "../ui/header-logo.tsx"
import { Link } from "../ui/link.tsx"
import type { Route } from "./+types/check-in-landing.route.ts"
import { CheckInState } from "./check-in-state.ts"
import { useCountdown } from "./ui/use-countdown.ts"
import { UserMenu } from "./ui/user-menu.tsx"

type PageData = {
  user?: UserData
  state: State
}

type UserData = {
  id: string
  username: string
  totalCheckIns: number
  totalMerchOwned: number
}

type ShowData = {
  id: string
  name: string
  venue: string
  startTime: number
}

type State = Data.TaggedEnum<{
  LiveShow: { show: ShowData }
  NextShow: { show: ShowData }
  NoShows: {}
}>

export const State = Data.taggedEnum<State>()

const toShow = (show: Identified<Show>): ShowData => ({
  id: show.id.value,
  name: show.value.name,
  venue: show.value.venue,
  startTime: show.value.checkInTime.startTime.epochMillis
})

export const loader = Handler.make<PageData>("check-in-landing", ({ shows, sessions, proofs, api }) =>
  CheckInState.make(shows, sessions, proofs, api).pipe(
    Effect.map(
      CheckInState.match({
        LiveShowNotFound: ({ nextShow }) =>
          Res.json({
            state: Option.match(nextShow, {
              onNone: () => State.NoShows(),
              onSome: (show) => State.NextShow({ show: toShow(show) })
            })
          }),
        NotLoggedIn: ({ show }) => Res.json({ state: State.LiveShow({ show: toShow(show) }) }),
        CheckedIn: () => Res.redirect(GlobalRoutes.profile.home),
        LoggedIn: ({ user, show, totalCheckIns, totalMerchOwned }) =>
          Res.json({
            user: { id: user.id.value, username: user.value.name, totalCheckIns, totalMerchOwned },
            state: State.LiveShow({ show: toShow(show) })
          })
      })
    )
  )
)

export default function CheckInLandingRoute(props: Route.ComponentProps) {
  const { user, state } = props.loaderData

  return (
    <PageContainer>
      {State.$match(state, {
        LiveShow: ({ show }) => <LiveShow show={show} user={user} />,
        NextShow: ({ show }) => <NextShow show={show} />,
        NoShows: () => <NoShows />
      })}
    </PageContainer>
  )
}

export const NO_SWEEPSTAKES_SHOWS = [
  "4e5ff0a2-6d95-468b-9730-7dc8bc8a2164", // Poland
  "3ae78051-4c99-4769-8368-1957ac8d3c6f" // Belgium
]

function LiveShow(props: { show: ShowData; user?: UserData }) {
  const analytics = useAnalytics()
  React.useEffect(() => {
    analytics.track_links("#check-in-button", Analytics.Event.CheckInStarted({ showId: props.show.id }))
  }, [])

  return (
    <>
      <HeaderLogo />
      {props.user && (
        <UserMenu
          username={props.user.username}
          totalCheckIns={props.user.totalCheckIns}
          totalMerchOwned={props.user.totalMerchOwned}
        />
      )}
      <div className="grow flex flex-col gap-3 items-center justify-center text-center">
        {!NO_SWEEPSTAKES_SHOWS.includes(props.show.id) ? (
          <>
            <h2 className="text-xs-upper">Check in for a chance to win</h2>
            <p className="text-[28px] leading-[33.6px] tracking-[-0.2px] max-w-72 h-[2lh]">
              Seat upgrade for today's show
            </p>
          </>
        ) : (
          <>
            <h2 className="text-xs-upper">Complete your Check-In to</h2>
            <p className="text-[28px] leading-[33.6px] tracking-[-0.2px] max-w-72 h-[2lh]">
              Unlock exclusive content
            </p>
          </>
        )}
      </div>
      <footer className="px-4 pb-6">
        <Link
          to={GlobalRoutes.checkIn.phoneNumber}
          id="check-in-button"
          className={UI.classForButton({ size: "lg", kind: "solid", className: "w-full" })}
        >
          Check in
        </Link>
      </footer>
    </>
  )
}

function NextShow(props: { show: ShowData }) {
  const revalidator = useRevalidator()
  const startDate = new Date(props.show.startTime)

  const formattedStartTime = new Intl.DateTimeFormat(navigator.language, {
    month: "long",
    day: "numeric"
  }).format(startDate)

  const { timeLeft, isEnded } = useCountdown(startDate)

  React.useEffect(() => {
    if (isEnded) {
      revalidator.revalidate()
    }
  }, [isEnded])

  return (
    <>
      <img
        className="mt-6 mx-auto"
        style={{ width: `${appConfig.assets.images.logo.width}px` }}
        src={appConfig.assets.images.logo.url}
        alt={`${appConfig.artist.name} logo`}
      />
      <div className="grow flex flex-col gap-3 items-center justify-center text-center">
        <h2 className="text-xs-upper">Showtime in</h2>
        <p className="text-[28px] tabular-nums">{timeLeft}</p>
      </div>
      <footer className="px-4 pb-10 flex flex-col items-center justify-center">
        <p className="text-xs-upper">{props.show.venue}</p>
        <p className="text-xs-upper">{formattedStartTime}</p>
      </footer>
    </>
  )
}

function NoShows() {
  return (
    <>
      <img
        className="mt-6 mx-auto"
        style={{ width: `${appConfig.assets.images.logo.width}px` }}
        src={appConfig.assets.images.logo.url}
        alt={`${appConfig.artist.name} logo`}
      />
      <div className="grow flex flex-col gap-3 items-center justify-center text-center">
        <p className="text-lg">No show found</p>
      </div>
    </>
  )
}
